/**
 * Intake API client — the only module that hits the Worker.
 *
 * Pure function: takes the envelope inputs, returns a validated IntakeResponse.
 * No React, no context. React integration lives in `hooks/usePostIntake.ts`.
 *
 * Contract per ADR-006 / pipeline spec:
 *   POST /api/intake
 *     body: { action, session_id, state, payload }
 *     response: { action, state_delta, reply, question, progress, complete, extras? }
 */

import type {
  Action,
  IntakeRequest,
  IntakeResponse,
  IntakeState,
} from './types';
import {
  ContractError,
  NetworkError,
  WorkerHttpError,
} from './errors';

export interface PostIntakeOptions<P> {
  action: Action;
  sessionId: string;
  state: IntakeState;
  payload: P;
  /** default 45s — Gemma 31b is 3–8s per turn; leave generous headroom */
  timeoutMs?: number;
  /** overridable for tests */
  fetchImpl?: typeof fetch;
  /** overridable base path (default `/api/intake`) */
  endpoint?: string;
}

const DEFAULT_TIMEOUT = 45_000;
const DEFAULT_ENDPOINT = '/api/intake';

export async function postIntake<P>(
  opts: PostIntakeOptions<P>,
): Promise<IntakeResponse> {
  const {
    action,
    sessionId,
    state,
    payload,
    timeoutMs = DEFAULT_TIMEOUT,
    fetchImpl = fetch,
    endpoint = DEFAULT_ENDPOINT,
  } = opts;

  const body: IntakeRequest<P> = {
    action,
    session_id: sessionId,
    state,
    payload,
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? err.message : 'Network request failed',
    );
  } finally {
    clearTimeout(timer);
  }

  let parsedBody: unknown = null;
  try {
    const text = await response.text();
    parsedBody = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    // leave parsedBody null — fall through to error handling below
  }

  if (!response.ok) {
    throw new WorkerHttpError(response.status, parsedBody);
  }

  if (!isIntakeResponse(parsedBody)) {
    throw new ContractError('Worker response did not match IntakeResponse shape');
  }

  return parsedBody;
}

// ---------------------------------------------------------------------------
// Minimal shape guard. The Worker is the source of truth for strict
// validation; this catches only gross malformations (e.g. HTML error pages
// returned by an intermediary, or a Worker regression that broke the shape).
// ---------------------------------------------------------------------------

function isIntakeResponse(v: unknown): v is IntakeResponse {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.action === 'string' &&
    o.state_delta !== null &&
    typeof o.state_delta === 'object' &&
    (o.reply === null || typeof o.reply === 'string') &&
    (o.question === null || typeof o.question === 'object') &&
    typeof o.progress === 'object' &&
    o.progress !== null &&
    typeof (o.progress as Record<string, unknown>).confirmed === 'number' &&
    typeof (o.progress as Record<string, unknown>).required_total === 'number' &&
    typeof o.complete === 'boolean'
  );
}
